import qs from 'qs'
import type React from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import api from 'services/api'
import Loading from '../Loading'
import './styles.scss'
import { highlightString } from 'utils/utils'
import InputSelectBase from '../InputSelectBase'
import ModalErrorBases from 'components/ModalErrorBases'
import { Device } from 'components/Device'
import { useFamily } from 'context/hooks/useFamily'
import SearchInput from 'components/Icons/SearchInput'
import ExitIcon from 'components/Icons/Exit'

export interface IFamiliesList {
  cpf: string
  name: string
  nis: string
  familyCode?: string
}

export interface IFilterBase {
  id: string
  name: string
  selected: boolean
}

function SearchFamilies() {
  const [familiesList, setFamiliesList] = useState<IFamiliesList[]>([])
  const [selectedFamily, setSelectedFamily] = useState<number>(0)
  const [showFamilies, setShowFamilies] = useState(false)
  
  const [textSearch, setTextSearch] = useState('')

  const [isLoading, setIsLoading] = useState(false)
  const [isSearchError, setIsSearchError] = useState(false)
  const [isNoSearchResults, setNoSearchResults] = useState(false)
  
  const [isErrorBases, setIsErrorBases] = useState(false)
  const [isReloadBases, setReloadBases] = useState(false)
  const [isLoadingBases, setIsLoadingBases] = useState(false)

  const [basesStates, setBasesStates] = useState<IFilterBase[]>([])

  const device = Device.useDevice()

  const navigate = useNavigate()

  const { bases, setBases } = useFamily()

  const timeoutId = useRef() as React.MutableRefObject<NodeJS.Timeout>
  const inputRef = useRef<HTMLInputElement>(null)
  const searchOptionsRef = useRef<HTMLElement>(null)


  function handleOnChangeBases(value: React.ChangeEvent<HTMLInputElement>) {
    const newBasesStates = basesStates.map((base: IFilterBase) => {
      if (base.name === value.target.name) {
        base.selected = !base.selected
      }
      return base
    })
    setBasesStates(newBasesStates)
  }

  async function handleChangeSearch(text: string) {
    setIsLoading(true)
    try {
      const idBases = bases.map(({ id }) => id)
      const textWithotDotsAndDashes = text.replace(/[.-]/g, "")
      await api
        .get('family/search', {
          params: {
            search: textWithotDotsAndDashes,
            bases: idBases,
          },
          paramsSerializer: (params: { search: string; bases: string[] }) => {
            return qs.stringify(params, { arrayFormat: 'repeat' })
          },
        })
        .then(({ data }) => {
          setFamiliesList([...data])
          if (data.length === 0 && text.length >= 3) {
            setNoSearchResults(true)
          }
        })
        .catch(async (error) => {
          setIsSearchError(true)
          return await Promise.reject(error)
        })
        .finally(() => {
          setIsLoading(false)
        })
    } catch (error) {
      return await Promise.reject(error)
    }
  }

  function handleKeyPress(event: React.KeyboardEvent<HTMLInputElement>) {
    inputRef.current?.focus()

    let selectedFamilyCurrent = selectedFamily

    if (event.code === 'ArrowDown') {
      event.preventDefault()
      if (selectedFamily < familiesList.length - 1) {
        setSelectedFamily(selectedFamily + 1)
        selectedFamilyCurrent = selectedFamilyCurrent + 1
      }
    }
    if (event.code === 'ArrowUp') {
      event.preventDefault()
      if (selectedFamily > 0) {
        setSelectedFamily(selectedFamily - 1)
        selectedFamilyCurrent = selectedFamilyCurrent - 1
      }
    }
    if (event.code === 'Enter' && familiesList[selectedFamilyCurrent] != null) {
      navigate(
        `geral?cpf=${familiesList[selectedFamilyCurrent].cpf}&nis=${
          familiesList[selectedFamilyCurrent].nis
        }&bases=${JSON.stringify(bases)}${
          familiesList[selectedFamilyCurrent].familyCode !== undefined
            ? `&familyCode=${
                familiesList[selectedFamilyCurrent].familyCode ?? ''
              }`
            : ''
        }`,
        { replace: true }
      )
    }
  }

  const searchOptions = useMemo(() => {
    if (isLoading && textSearch.length > 0) { // retorno quando estiver fazendo a busca
      return <Loading size={'small'} styles={{ marginTop: '1rem' }} />
    } else if (isSearchError && textSearch.length > 0) { // retorno quando dê algum erra na busca
      return <i>Ocorreu um erro na busca!</i>
    } else if (isNoSearchResults && textSearch.length > 0) { // retorno quando a busca não retornar nada
      return (
        <i>{`Sem resultados para "${textSearch}" nas bases selecionadas.`}</i>
      )
    }
    return familiesList?.map((family: IFamiliesList, index: number) => { // retorno padrão, quando houver dados
      return (
        <Link
          to={`geral?cpf=${family.cpf}&nis=${
            family.nis
          }&bases=${JSON.stringify(bases)}${
            family.familyCode !== undefined
              ? `&familyCode=${family.familyCode}`
              : ''
          }`}
          key={`${family.cpf}${family.nis}${family.name}`}
        >
          <div
            className={(index === selectedFamily ? 'selected' : '')}
          >
            <h1>
              {highlightString(
                  family.name.replaceAll('  ', ' ').toLowerCase(),
                  textSearch
              )}
            </h1>
            <ul>
              <li>
                CPF:{' '}
                {highlightString(family.cpf, textSearch)}
              </li>

              <li>
                NIS:{' '}
                {highlightString(family.nis, textSearch)}
              </li>
              {
                family.familyCode !== undefined && (
                  <li>
                    Código familiar:{' '}
                    {highlightString(family.familyCode, textSearch)}
                  </li>
                )
              }
            </ul>
          </div>
        </Link>
      )
    })
  }, [
    isLoading,
    textSearch,
    isSearchError,
    familiesList,
    isNoSearchResults,
    selectedFamily
  ])

  // esse useEffect é chamado sempre que o usuário muda algo na busca, texto ou bases.
  // faz a busca somente se o texto tiver menos de 3 palavras, e espera 0,8 segundos pra fazer a busca,
  // dando a chanche do usuário modificar o texto, sem precisar fazer uma nova requisição
  useEffect(() => {
    if (textSearch.trim().length === 0 || textSearch.length < 3) {
      setFamiliesList([])
      return
    } else {
      timeoutId.current = setTimeout(() => {
        void handleChangeSearch(textSearch)
      }, 800)
    }

    return () => {
      clearTimeout(timeoutId.current)
      setIsSearchError(false)
      setNoSearchResults(false)
    }
  }, [textSearch, bases])

  // Esse useEffect é responsável por deixar o elemento selecionado na altura do scroll
  useEffect(() => {
    inputRef.current?.focus()
    const selected = document.querySelector('.container-search__options')
    if (selected?.firstElementChild?.clientHeight !== undefined) {
      selected.scrollTop =
        selected.firstElementChild.clientHeight * selectedFamily -
        selected.firstElementChild.clientHeight
    }
  }, [selectedFamily])

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        !(inputRef.current?.contains(event.target as Node) ?? false) &&
        !(searchOptionsRef.current?.contains(event.target as Node) ?? false)
      ) {
        setShowFamilies(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [searchOptionsRef])

  useEffect(() => {
    setIsLoadingBases(true)
    setIsErrorBases(true)
    api
      .get('/databases')
      .then(({ data }) => {
        if (data?.length > 0) {
          const basesResponse = data.map(
            (base: { name: string; id: string }) => {
              return {
                name: base.name,
                selected: false,
                id: base.id,
              }
            }
          )

          const newBases = [...basesStates, ...basesResponse]

          setBasesStates(newBases)

          setIsErrorBases(false)
        }
      })
      .catch(() => {
        setIsErrorBases(true)
      })
      .finally(() => {
        setIsLoadingBases(false)
      })
  }, [isReloadBases])

  useEffect(() => {
    // se todos os filtros estiverem desmarcados
    const selectedBases = basesStates.filter(
      (base: IFilterBase) => base.selected
    )
    if (selectedBases.length === 0) {
      const newBases = basesStates.map(({ id, name }: IFilterBase) => ({
        id,
        name,
      }))
      setBases(newBases)
    } else {
      setBases(selectedBases.map(({ id, name }: IFilterBase) => ({ id, name })))
    }
  }, [basesStates])

  // rendering conditions
  if (isLoadingBases)
    return <Loading title="Verificando bases..." colors={['#fff', '#fff']} />
  if (isErrorBases)
    return (
      <ModalErrorBases
        key={'moral-error'}
        visible={isErrorBases}
        handleOk={() => {
          setReloadBases(!isReloadBases)
        }}
      />
    )
  // return default
  return (
    <div className={`container-search ${device.prefix() ?? 'undefined'}`}>
      <h2>Digite nome, código familiar, CPF ou NIS do responsável</h2>
      <div
        className={`container-search__input ${device.prefix() ?? 'undefined'}`}
      >
        <input
          onKeyDownCapture={handleKeyPress} // captura as teclas de seta quando pressionadas nesse input e muda a familia selecionada
          ref={inputRef}
          type="text"
          onChange={(event) => {
            setTextSearch(event.target.value)
          }}
          onFocus={() => {
            setShowFamilies(true)
          }}
          spellCheck="false"
          placeholder={
            device.prefix() === 'xs' || device.prefix() === undefined
              ? 'Pesquisar'
              : 'Digite nome, código familiar, CPF ou NIS do responsável'
          }
          value={textSearch}
          maxLength={130}
          onKeyDown={(event: React.KeyboardEvent) => {
            if (event.key === 'Escape') {
              setTextSearch('')
              inputRef.current?.blur()
            }
          }}
        />
        <SearchInput />
        {
          textSearch.length > 0 && (
            <ExitIcon click={()=> setTextSearch('')} />
          )
        }
        <InputSelectBase
          filterBases={basesStates}
          handleFilter={handleOnChangeBases}
          isLoadingBases={isLoadingBases}
        />
      </div>
      {showFamilies && (
        <section
          className={`container-search__options ${
            device.prefix() ?? 'undefined'
          }`}
          ref={searchOptionsRef}
        >
          {searchOptions}
        </section>
      )}
    </div>
  )
}

export default SearchFamilies
